import React, { useState, useRef } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import Select, { createFilter } from 'react-select';
import styles from './styles';
import { CustomLabel, MenuList, Option, CheckboxOption, Input } from './components';
import Chip from '@material-ui/core/Chip';
import { hideScrollBar } from './components/customScroll';

const Dropdown = ({ onChange, styleType, filterConfig, label, value, disabled, ...props }) => {
  const selectRef = useRef();
  const isMulti = styleType === 'checkbox';
  const [announceText, setAnnounceText] = useState('');
  const [inputChange, setInputChange] = useState('');
  const [searchResultText, setSearchResultText] = useState('');
  const [searchResult, setSearchResult] = useState({ count: props.options.length });
  const [allowScrollToIndex, setAllowScrollToIndex] = useState(false);

  const getTypeOverrides = () => {
    if (isMulti) {
      return {
        closeMenuOnSelect: false,
        hideSelectedOptions: false,
      };
    }
  };

  const handleRemoveChip = id => {
    const newValues = value.filter(item => item.id !== id);
    formatSelectedValues(newValues);
  };
  const id = moment();
  const handleChange = selectedValue => {
    if (selectedValue) {
      setAnnounceText(`Option ${selectedValue.label} selected`);
    }
    formatSelectedValues(selectedValue);
  };

  const handleOnFocusOption = options => {
    /** Small delay required here becasue after input change
     * immidiately the onOptionFocus event sent so
     * in order to display the search result messsage
     * this delay is required. Once displayed it is then reset.
     */
    setTimeout(() => setSearchResultText(''), 1000);

    let selectedIndex;
    const selectedOtions = options.filter((option, index) => {
      option.props.isFocused && (selectedIndex = index);
      return option.props.isFocused;
    });
    const selectedOption = selectedOtions.length > 0 && selectedOtions[0];
    const selectedOptionAnnounceText = `${selectedOption.props.label}, ${selectedIndex + 1} of ${options.length}`;
    announceText !== selectedOptionAnnounceText && setAnnounceText(selectedOptionAnnounceText);
  };

  const listRenderer = props => (
    <MenuList
      handleOnFocusOption={handleOnFocusOption}
      allowScrollToIndex={allowScrollToIndex}
      enableScrollToIndex={enableScrollToIndex}
      {...props}
    />
  );

  const handleInputChange = event => {
    setInputChange(event);
  };

  const handleSearchResult = result => {
    if (result.count && result.count !== searchResult.count) {
      setSearchResult(result);
      setSearchResultText(`${result.count} results available for search term ${inputChange}.`);
    }
  };

  const handleOnFocus = () => {
    hideScrollBar(true, props.parentElem);
  };

  const handleOnBlur = () => {
    setAnnounceText('');
    hideScrollBar(false, props.parentElem);
  };

  const formatSelectedValues = selectedValue => {
    if (!onChange) return;
    if (isMulti) {
      onChange({
        target: {
          name: props.name || null,
          id: props.id || null,
          value: selectedValue,
        },
      });
    } else {
      onChange({
        target: {
          name: props.name || null,
          id: props.id || null,
          ...selectedValue,
          value: selectedValue && selectedValue.value,
        },
      });
    }
  };

  const enableScrollToIndex = value => allowScrollToIndex !== value && setAllowScrollToIndex(value);

  return (
    <div className="dropdown-control-container">
      <div aria-hidden="true">
        <Select
          id={id}
          {...props}
          className={'dropdown-custom'}
          classNamePrefix="dropdown-control"
          ref={selectRef}
          selectRef={selectRef}
          components={{
            MenuList: listRenderer,
            ValueContainer: CustomLabel,
            Option: isMulti ? CheckboxOption : Option,
            Input: Input,
          }}
          menuPortalTarget={document.getElementById(id)}
          filterOption={createFilter(filterConfig)}
          styles={styles}
          value={value === undefined ? '' : value && props.options.find(option => option.value === value)}
          onChange={handleChange}
          onInputChange={handleInputChange}
          isClearable
          openMenuOnFocus={false}
          {...getTypeOverrides(styleType)}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          onMenuOpen={() => hideScrollBar(true, props.parentElem)}
          onMenuClose={() => hideScrollBar(false, props.parentElem)}
          placeholder={label}
          isMulti={isMulti}
          aria-label={label}
          isDisabled={disabled}
          screenReaderStatus={status => handleSearchResult(status)}
          onKeyDown={() => enableScrollToIndex(true)}
        />
      </div>

      <div className="announce-message" aria-live="polite">
        {searchResultText.length > 0 && searchResultText}
        {announceText.length > 0 && announceText}
      </div>

      {isMulti && value && value.length > 0 && (
        <div className="chips-container">
          {value.map(item => (
            <Chip label={item.label} aria-label={item.label} onDelete={() => handleRemoveChip(item.id)} />
          ))}
        </div>
      )}
    </div>
  );
};

Dropdown.defaultProps = {
  styleType: 'default',
  filterConfig: {
    ignoreCase: true,
    ignoreAccents: false,
    trim: false,
    matchFromStart: false,
  },
  tabSelectsValue: false,
};

/** See more props values here: https://react-select.com/props#prop-types*/
Dropdown.propTypes = {
  /** apply a className to the control */
  className: PropTypes.string,
  /** Dropdown type 'default' or 'checkbox' */
  styleType: PropTypes.oneOf(['default', 'checkbox']),
  /** subscribe to change events */
  onChange: PropTypes.func.isRequired,
  /** specify the options the user can select from */
  options: PropTypes.PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.any,
      value: PropTypes.any,
    }),
  ).isRequired,
  /** disable the control*/
  isDisabled: PropTypes.bool,
  /** If `true`, Dropdown will show loader till the data is loaded */
  isLoading: PropTypes.bool,
  /** If `true`, Dropdown will show cross icon to delete the value*/
  isClearable: PropTypes.bool,
  /** allow the user to search for matching options */
  isSearchable: PropTypes.bool,
  /** It will show label for the dropdown*/
  placeholder: PropTypes.string,
  /**  Text to display when there are no options*/
  noOptionsMessage: PropTypes.func,
  /** control the current value */
  value: PropTypes.any,
  /** allow the user to select multiple values*/
  isMulti: PropTypes.bool,
  /** generate an HTML input with this name, containing the current value */
  name: PropTypes.string,
  /** set the initial value of the control */
  defaultValue: PropTypes.any,
};

export default Dropdown;
